import {
  CheckIcon,
  DeleteIcon,
  EditIcon,
  HamburgerIcon
} from "@chakra-ui/icons";
import { Flex, Input } from "@chakra-ui/react";
import { CustomTableColorColumn } from "components/dynamic-table/color-table";
import { Dispatch, SetStateAction } from "react";
import { openEditColumnModal } from "./edit-column-modal";
import { normalizeText } from "utils/filter-array-by";
import { IconButtonWithLoading } from "../mailing-table/icon-button-with-loading";

export class CSVFileReader {
  constructor(private file: File | undefined) {}

  async csvToJSON(
    {
      separator,
      headersParse,
      normalizeHeaders = false
    }: {
      headersParse?: { fieldInCSV: string; fieldInJson: string }[];
      separator: string;
      normalizeHeaders?: boolean;
    } = { separator: ";", normalizeHeaders: false }
  ): Promise<{ [k: string]: string }[] | undefined> {
    if (!this.file) throw new Error("File not found");
    const text = await this.file!.text();
    const lines = text.split("\n").filter((l) => l.trim() !== "");

    // Make Header
    const jsonResult = lines.reduce((json, line, i) => {
      if (i !== 0) {
        // Split Line
        const currSplitedLine = line.split(separator);
        // Make Object model with indexes ref
        let currentJSONObject = lines[0]
          ?.split(separator)
          .reduce((acc, currCsvField, i) => {
            const field = normalizeHeaders
              ? normalizeText(currCsvField)
              : currCsvField;
            if (!field) return acc; // Skip empty headers
            const parsedField: any = headersParse?.find(
              (curr) => curr.fieldInCSV === field
            ) || { fieldInCSV: field, fieldInJson: field };

            acc[parsedField.fieldInJson] = i;
            return acc;
          }, {} as any);
        // Change indexes to respective content
        Object.keys(currentJSONObject).forEach((key) => {
          const targetIndex = currentJSONObject[key];
          currentJSONObject[key] = currSplitedLine[targetIndex];
        });
        json.push(currentJSONObject);
      }
      return json;
    }, [] as any[]);

    return jsonResult;
  }

  async jsonToCSV({
    json,
    headersParse,
    sep = ";"
  }: {
    json?: { [k: string]: string }[];
    headersParse?: {
      name: string;
      field?: string;
      render?: (line: { [k: string]: string }) => string;
    }[];
    sep?: string;
  }) {
    if (!this.file) throw new Error("File not found");
    if (!json) json = await this.csvToJSON()!;

    let headers: {
      name: string;
      field?: string;
      render?: (line: { [k: string]: string }) => string;
    }[] =
      headersParse ||
      Object.keys(json![0]).map((key) => ({ name: key, field: key }));
    let content = "";

    let constructHeader = `${headers?.map(({ name }) => name).join(sep)}\n`; // header
    content += constructHeader;
    let constructContent = json!
      .map((line) => {
        return headers
          .map(
            ({ field, render }) =>
              render ? render(line) : `${line[field ?? ""] ?? ""}` // making cell
          )
          .join(sep); // making line
      })
      .join("\n"); // making lines group
    content += constructContent;

    return content;
  }
  async getHeaders(): Promise<string[]> {
    if (!this.file) throw new Error("File not found");

    const json = await this.csvToJSON();

    return Object.keys(json![0]);
  }
}

export function constructColumns({
  editIndex,
  headers,
  setEditIndex,
  setRows,
  edit,
  readOnlyColumns = [],
  onDeleteRow,
  ...params
}: {
  headers: string[];
  setRows: Dispatch<SetStateAction<any[]>>;
  setEditIndex: Dispatch<
    SetStateAction<{ index: number; field: string } | undefined>
  >;
  editIndex: { index: number; field: string } | undefined;
  edit: boolean;
  delete?: boolean;
  readOnlyColumns?: string[];
  onDeleteRow?: (rowIndex: number) => void;
}) {
  const handleEdit = async (row: any, header: string) => {
    const isEditing =
      editIndex?.index === row.index && header === editIndex?.field;
    if (isEditing) {
      setEditIndex(undefined);
    } else {
      setEditIndex({ index: row.index, field: header });
      setTimeout(
        () => document.getElementById(`input-${header}-${row.index}`)?.focus(),
        100
      );
    }
  };

  const dataColumns: CustomTableColorColumn[] = headers.map(
    (header): CustomTableColorColumn => {
      return {
        name: header,
        headerStyles: { px: "10px" },
        cellStyles: (row) => ({
          px: "10px",
          _hover:
            edit && !readOnlyColumns.includes(header) ? { bg: "gray.100" } : {},
          onClick:
            edit && !readOnlyColumns.includes(header)
              ? () => handleEdit(row, header)
              : undefined
        }),
        icon: <HamburgerIcon ml={1} w="4" h="4" />,
        onClick:
          edit && !readOnlyColumns.includes(header)
            ? () => {
                const isCEPHeader = header === "CEP";
                const headersSet = new Set(headers);
                openEditColumnModal({
                  header,
                  onConfirm: (newValue: string) => {
                    setRows((prev) =>
                      prev.map((currentRow) => ({
                        ...currentRow,
                        [header]: newValue
                      }))
                    );
                    setEditIndex(undefined);
                  },
                  ...(isCEPHeader && {
                    onConfirmCEP: (cepData) => {
                      const addressMapping = [
                        { col: "ENDERECO", value: cepData.logradouro },
                        { col: "NUMERO", value: "" },
                        { col: "BAIRRO", value: cepData.bairro },
                        { col: "UF", value: cepData.uf },
                        { col: "CIDADE", value: cepData.localidade ?? "" }
                      ];
                      setRows((prev) =>
                        prev.map((currentRow) => {
                          const updates: Record<string, string> = {};
                          addressMapping.forEach(({ col, value }) => {
                            if (headersSet.has(col)) {
                              updates[col] = value;
                            }
                          });
                          return { ...currentRow, ...updates };
                        })
                      );
                    }
                  })
                });
              }
            : undefined,
        render: (row) => {
          const size = "20px";
          const isEditing =
            editIndex?.index === row.index && header === editIndex?.field;
          const isReadOnly = readOnlyColumns.includes(header);

          return (
            <Flex w="100%" p="5px">
              {editIndex?.index === row.index &&
              header === editIndex?.field &&
              edit &&
              !isReadOnly ? (
                <Input
                  id={`input-${header}-${row.index}`}
                  onChange={(e) =>
                    setRows((prev) => {
                      return prev.map((crrRow) => {
                        if (crrRow.index === row.index) {
                          return { ...crrRow, [header]: e.target.value };
                        }
                        return crrRow;
                      });
                    })
                  }
                  fontSize="12px"
                  minW="40px"
                  px="4px"
                  variant="outline-custom"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleEdit(row, header);
                  }}
                  value={row[header]}
                />
              ) : (
                <Flex mr="auto">{row[header]}</Flex>
              )}

              {edit && !isReadOnly ? (
                isEditing ? (
                  <IconButtonWithLoading
                    tooltipMessage={isEditing ? "Salvar" : "Editar"}
                    aria-label=""
                    size="xs"
                    mt="-10px"
                    mb="auto"
                    ml="5px"
                    w={size}
                    h={size}
                    minW={size}
                    icon={isEditing ? <CheckIcon /> : <EditIcon />}
                    onClick={() => handleEdit(row, header)}
                  />
                ) : (
                  <EditIcon display="block" mt="-10px" mb="auto" ml="5px" />
                )
              ) : null}
            </Flex>
          );
        }
      };
    }
  );

  if (edit && onDeleteRow && params?.delete) {
    dataColumns.push({
      name: "Ações",
      headerStyles: { px: "10px" },
      cellStyles: () => ({ px: "10px" }),
      render: (row) => (
        <IconButtonWithLoading
          tooltipMessage="Remover linha"
          aria-label="remover linha"
          variant="danger"
          icon={<DeleteIcon />}
          onClick={async () => {
            onDeleteRow(row.index);
          }}
        />
      )
    });
  }

  return dataColumns;
}
